import { useCallback, useEffect, useState } from 'react';
import * as CookieConsent from 'vanilla-cookieconsent';
import { cookies } from '../config/cookies';

let cookieConsentInitialized = false;
const COOKIE_CONSENT_SYNC_KEY = 'monery_cookie_consent_sync';
type ConsentSyncSource = 'initial' | 'local' | 'external';

export const getCookieConsentState = (cookie: CookieConsent.CookieValue) => {
    const categories = Array.isArray(cookie.categories) ? cookie.categories : [];
    return {
        requiredConsent: categories.includes('necessary'),
        analyticsConsent: categories.includes('analytics'),
        advertisingConsent: categories.includes('advertising'),
    };
};

export const showCookiePreferences = () => {
    CookieConsent.showPreferences();
};

const useCookies = (isApp: boolean) => {
    const [cookieConsentReady, setCookieConsentReady] = useState(false);
    const [consentSyncSource, setConsentSyncSource] = useState<ConsentSyncSource>('initial');
    const [requiredCookieAccepted, setRequiredCookieAccepted] = useState(false);
    const [analyticsCookieAccepted, setAnalyticsCookieAccepted] = useState(false);
    const [advertisingCookieAccepted, setAdvertisingCookieAccepted] = useState(false);

    const syncCookieConsent = useCallback((cookie: CookieConsent.CookieValue, source: ConsentSyncSource) => {
        const state = getCookieConsentState(cookie);

        setRequiredCookieAccepted(state.requiredConsent);
        setAnalyticsCookieAccepted(state.analyticsConsent);
        setAdvertisingCookieAccepted(state.advertisingConsent);
        setCookieConsentReady(true);
        setConsentSyncSource(source);
    }, []);

    const notifyOtherTabs = useCallback(() => {
        try {
            window.localStorage.setItem(COOKIE_CONSENT_SYNC_KEY, new Date().toISOString());
        } catch (error) {
            // Consent still works when browser storage is unavailable.
        }
    }, []);

    const handleShowPreferences = useCallback(() => {
        showCookiePreferences();
    }, []);

    useEffect(() => {
        const handleStorage = (event: StorageEvent) => {
            if (event.key === COOKIE_CONSENT_SYNC_KEY) syncCookieConsent(CookieConsent.getCookie(), 'external');
        };
        window.addEventListener('storage', handleStorage);

        if (cookieConsentInitialized) {
            syncCookieConsent(CookieConsent.getCookie(), 'initial');
            if (isApp) {
                CookieConsent.hide();
            }
            return () => window.removeEventListener('storage', handleStorage);
        }

        cookieConsentInitialized = true;
        CookieConsent.run({
            ...cookies,
            onConsent: ({ cookie }) => {
                syncCookieConsent(cookie, 'initial');
                notifyOtherTabs();
            },
            onChange: ({ cookie }) => {
                syncCookieConsent(cookie, 'local');
                notifyOtherTabs();
            },
        }).then(() => {
            syncCookieConsent(CookieConsent.getCookie(), 'initial');
            if (isApp) {
                CookieConsent.hide();
            }
        });
        return () => window.removeEventListener('storage', handleStorage);
    }, [isApp, notifyOtherTabs, syncCookieConsent]);

    return {
        advertisingCookieAccepted,
        analyticsCookieAccepted,
        cookieConsentReady,
        consentSyncSource,
        optionalCookieAccepted: analyticsCookieAccepted,
        requiredCookieAccepted,
        handleShowPreferences,
    };
};

export default useCookies;
